import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Smartphone,
  Activity,
  AlertCircle,
  Tag,
  Clock,
  RefreshCw
} from 'lucide-react';
import { 
  DispositivoSsem, 
  DispositivoSsemEstadisticas 
} from '../../interfaces/DispositivoSsemInterface';
import { 
  getAllDispositivosSsem, 
  getDispositivosSsemEstadisticas 
} from '../../apis/dispositivoSsemApi';
import { AuthContext } from '../../context/AuthContext';

export const DispositivosSsem: React.FC = () => {
  const authContext = useContext(AuthContext);
  const [dispositivos, setDispositivos] = useState<DispositivoSsem[]>([]);
  const [allDispositivos, setAllDispositivos] = useState<DispositivoSsem[]>([]);
  const [filteredDispositivos, setFilteredDispositivos] = useState<DispositivoSsem[]>([]);
  const [estadisticas, setEstadisticas] = useState<DispositivoSsemEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Cargar datos iniciales
  useEffect(() => {
    loadDispositivos();
    loadEstadisticas();
  }, []);

  // Filtrar dispositivos cuando cambie el término de búsqueda
  useEffect(() => {
    filterDispositivos();
  }, [searchTerm, allDispositivos]);

  // Paginar los dispositivos filtrados
  useEffect(() => {
    paginateFilteredDispositivos();
  }, [filteredDispositivos, currentPage]);

  const loadDispositivos = async () => {
    try {
      setLoading(true);
      const response = await getAllDispositivosSsem(1, 1000);
      setAllDispositivos(response.data);
    } catch (error) {
      console.error('Error cargando dispositivos:', error);
      setAllDispositivos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const response = await getDispositivosSsemEstadisticas();
      setEstadisticas(response.estadisticas);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  // Función para filtrar dispositivos localmente
  const filterDispositivos = () => {
    if (!searchTerm.trim()) {
      setFilteredDispositivos(allDispositivos);
      return;
    }

    const filtered = allDispositivos.filter(dispositivo => {
      const searchLower = searchTerm.toLowerCase();
      return (
        dispositivo.imei_ssem.toLowerCase().includes(searchLower) ||
        dispositivo.marca_ssem.toLowerCase().includes(searchLower) ||
        dispositivo.modelo_ssem.toLowerCase().includes(searchLower) ||
        dispositivo.color_ssem.toLowerCase().includes(searchLower) ||
        dispositivo.estatus_ssem.toLowerCase().includes(searchLower) ||
        (dispositivo.tag_blac?.name_device && dispositivo.tag_blac.name_device.toLowerCase().includes(searchLower)) ||
        (dispositivo.tag_blac?.plate && dispositivo.tag_blac.plate.toLowerCase().includes(searchLower)) ||
        (dispositivo.observaciones && dispositivo.observaciones.toLowerCase().includes(searchLower))
      );
    });

    setFilteredDispositivos(filtered);
    setCurrentPage(1);
  };

  // Función para paginar los dispositivos filtrados
  const paginateFilteredDispositivos = () => {
    const itemsPerPage = 10;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedDispositivos = filteredDispositivos.slice(startIndex, endIndex);
    
    setDispositivos(paginatedDispositivos);
    setTotalPages(Math.ceil(filteredDispositivos.length / itemsPerPage));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = async () => {
    await loadDispositivos();
    await loadEstadisticas();
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  const getStatusColor = (estatus: string) => {
    switch (estatus) {
      case 'activo':
        return 'text-green-400 bg-green-400/10';
      case 'inactivo':
        return 'text-red-400 bg-red-400/10';
      case 'mantenimiento':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'perdido':
        return 'text-red-600 bg-red-600/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  const styles = `
    .dispositivos-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      margin-bottom: 2rem;
    }

    .page-title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 0.5rem;
    }

    .page-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1.1rem;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      transition: all 0.3s ease;
    }

    .stat-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 122, 255, 0.3);
      transform: translateY(-2px);
    }

    .stat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .stat-title {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
      font-weight: 500;
    }

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      color: #007AFF;
    }

    .controls-section {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
    }

    .search-container {
      position: relative;
      flex: 1;
      max-width: 400px;
    }

    .search-input {
      width: 100%;
      padding: 12px 16px 12px 48px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: #007AFF;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255, 255, 255, 0.5);
    }

    .action-buttons {
      display: flex;
      gap: 1rem;
    }

    .btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
    }

    .btn-primary {
      background: #007AFF;
      color: white;
    }

    .btn-primary:hover {
      background: #0056CC;
      transform: translateY(-1px);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-small {
      padding: 6px 12px;
      font-size: 12px;
    }

    .table-container {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      overflow: hidden;
    }

    .table {
      width: 100%;
      border-collapse: collapse;
    }

    .table th {
      background: rgba(255, 255, 255, 0.1);
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .table td {
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.8);
    }

    .table tr:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    .tag-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .tag-name {
      font-weight: 500;
      color: #007AFF;
    }

    .tag-plate {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
      font-family: 'Courier New', monospace;
    }

    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-top: 2rem;
      padding: 1rem;
    }

    .pagination-btn {
      padding: 8px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .pagination-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
    }

    .pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .loading-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 4rem;
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .empty-icon {
      margin: 0 auto 1rem;
      opacity: 0.5;
    }

    .search-results-indicator {
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .page-title {
        font-size: 2rem;
      }

      .controls-section {
        flex-direction: column;
        align-items: stretch;
      }

      .search-container {
        max-width: none;
      }

      .action-buttons {
        justify-content: center;
      }

      .table-container {
        overflow-x: auto;
      }

      .table {
        min-width: 900px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="dispositivos-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <h1 className="page-title">Dispositivos Skyangel</h1>
            <p className="page-subtitle">
              Gestión de dispositivos Skyangel
            </p>
          </div>

          {/* Estadísticas */}
          {estadisticas && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Total</span>
                  <Smartphone size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.total_dispositivos}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem' }}>
                  {searchTerm ? `${filteredDispositivos.length} mostrados` : 'Dispositivos'}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Activos</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                </div>
                <div className="stat-value">{estadisticas.dispositivos_activos}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Con Tag</span>
                  <Tag size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.dispositivos_con_tag}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Mantenimiento</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
                </div>
                <div className="stat-value">{estadisticas.dispositivos_mantenimiento}</div>
              </div>
            </div>
          )}

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar por IMEI, marca, modelo, color, estado o tag..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>

            <div className="action-buttons">
              <button
                onClick={handleRefresh}
                className="btn btn-secondary"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>

              {authContext.tienePermiso('dispositivos.gestionar') && (
                <Link to="/dispositivos-ssem/crear" className="btn btn-primary">
                  <Plus size={16} />
                  Nuevo Dispositivo
                </Link>
              )}
            </div>
          </div>

          {/* Indicador de resultados */}
          {searchTerm && (
            <div className="search-results-indicator" style={{
              padding: '1rem 2rem',
              background: 'rgba(0, 122, 255, 0.1)',
              border: '1px solid rgba(0, 122, 255, 0.2)',
              borderRadius: '12px',
              marginBottom: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem'
            }}>
              🔍 Mostrando {filteredDispositivos.length} resultado{filteredDispositivos.length !== 1 ? 's' : ''} para "{searchTerm}"
            </div>
          )}

          {/* Tabla de Dispositivos */}
          <div className="table-container">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner" />
              </div>
            ) : dispositivos.length === 0 ? (
              <div className="empty-state">
                <Smartphone className="empty-icon" size={48} />
                <h3>
                  {searchTerm ? 'No se encontraron resultados' : 'No hay dispositivos registrados'}
                </h3>
                <p>
                  {searchTerm 
                    ? `No hay dispositivos que coincidan con "${searchTerm}"`
                    : 'Comienza creando tu primer dispositivo Skyangel'
                  }
                </p>
                {!searchTerm && authContext.tienePermiso('dispositivos.gestionar') && (
                  <Link to="/dispositivos-ssem/crear" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                    <Plus size={16} />
                    Crear Primer Dispositivo
                  </Link>
                )}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>IMEI</th>
                    <th>Dispositivo</th>
                    <th>Estado</th>
                    <th>Tag Asignada</th>
                    <th>Fecha Asignación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {dispositivos.map((dispositivo) => (
                    <tr key={dispositivo.id}>
                      <td>
                        <code style={{ 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          {dispositivo.imei_ssem}
                        </code>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {dispositivo.marca_ssem} {dispositivo.modelo_ssem}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                            {dispositivo.color_ssem} • {dispositivo.ram_ssem} • {dispositivo.capacidad_almacenamiento_ssem}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusColor(dispositivo.estatus_ssem)}`}>
                          <div style={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            backgroundColor: 
                              dispositivo.estatus_ssem === 'activo' ? '#10B981' :
                              dispositivo.estatus_ssem === 'mantenimiento' ? '#F59E0B' :
                              '#EF4444'
                          }} />
                          {dispositivo.estatus_ssem}
                        </span>
                      </td>
                      <td>
                        {dispositivo.tag_blac ? (
                          <div className="tag-info">
                            <span className="tag-name">{dispositivo.tag_blac.name_device}</span>
                            <span className="tag-plate">{dispositivo.tag_blac.plate}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Sin tag asignada</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {formatDateTime(dispositivo.fecha_asignacion_tag)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Link
                            to={`/dispositivos-ssem/${dispositivo.id}`}
                            className="btn btn-secondary btn-small"
                          >
                            <Eye size={14} />
                            Ver
                          </Link>
                          {authContext.tienePermiso('dispositivos.gestionar') && (
                            <Link
                              to={`/dispositivos-ssem/${dispositivo.id}/editar`}
                              className="btn btn-primary btn-small"
                            >
                              <Edit size={14} />
                              Editar
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Anterior
              </button>

              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Página {currentPage} de {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
